/* eslint-disable no-use-context/no-use-context */
import type { Dispatch } from "react";
import { useContext } from "react";

import { useDispatch } from "react-redux";

import { useTheme } from "@busbud/design-system-components/dist/styles/useTheme";
import type { BreakpointKeys } from "@busbud/design-system-components/dist/types/tokens/breakpoints";
import { useMediaQuery } from "@busbud/design-system-components/dist/utilities/useMediaQuery";

import type {
  AppCommonContext,
  ApplicationContext
} from "@app/context/app-context";
import AppContext from "@app/context/app-context";
import { LiteAppContext } from "@app/context/lite-app-context";

import type { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";

/** Common shared context available to both client and server */
export const useAppContext = (): AppCommonContext => useContext(AppContext);

/** Lightweight shared context available to both client and server for search and landing page entries */
export const useLiteAppContext = (): LiteAppContext =>
  useContext(LiteAppContext);

/** Context available during client-side rendering and hydration */
export const useClientAppContext = () => useAppContext() as ApplicationContext;

const useIsViewport = (key: BreakpointKeys) => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down(key), {
    noSsr: true // Needed otherwise it is rendered twice, first time with false, the value of the server, and a second time with the CSR resolved value
  });
};
export const useIsXsViewport = () => useIsViewport("xs");
export const useIsSmViewport = () => useIsViewport("sm");
export const useIsMdViewport = () => useIsViewport("md");

const useIsOnlyViewport = (key: BreakpointKeys) => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.only(key), {
    noSsr: true // Needed otherwise it is rendered twice, first time with false, the value of the server, and a second time with the CSR resolved value
  });
};
export const useIsOnlySmViewport = () => useIsOnlyViewport("sm");
export const useIsOnlyMdViewport = () => useIsOnlyViewport("md");
export const useIsOnlyLgViewport = () => useIsOnlyViewport("lg");

export type appDispatch = Dispatch<AnyAction> &
  ThunkDispatch<any, undefined, AnyAction>;
export const useAppDispatch: () => appDispatch = useDispatch;
